import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Star, CheckCircle, Bookmark, BookmarkCheck } from "lucide-react";
import "../styles/categoryDetail.css";
import { aiToolsData } from "../data/aiTools";
import { getRealUrl, getRealIcon, getRealDescription, getRealPricing } from "../utils/aiToolsHelpers";

const API_BASE = process.env.REACT_APP_API_BASE || 
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" 
    ? "http://localhost:5005/api" 
    : "https://vblogai-backend.onrender.com/api");

// getRealUrl is imported from helpers

export default function CategoryDetail() {
  const { categoryId } = useParams();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState({});

  useEffect(() => {
    const fetchTools = async () => {
      try {
        const response = await fetch(`${API_BASE}/ai-tools`);
        if (!response.ok) throw new Error("Failed to fetch tools");
        const data = await response.json();
        
        // Find specific tools belonging to this category
        // In the backend schema, tools are grouped or can be filtered by category string
        let categoryTools = [];
        data.forEach(cat => {
          if (cat.category.toLowerCase() === categoryId.toLowerCase()) {
            categoryTools = cat.tools;
          }
        });
        
        // Fallback: Check flat list
        if (categoryTools.length === 0) {
          const flat = data.flatMap(c => c.tools);
          categoryTools = flat.filter(t => 
            (t.category || "").toLowerCase() === categoryId.toLowerCase() ||
            (t.categories || []).some(c => c.toLowerCase() === categoryId.toLowerCase())
          );
        }

        setTools(categoryTools);
      } catch (err) {
        console.error("Error loading category detail from API, falling back to local data:", err.message);
        
        let categoryTools = [];
        aiToolsData.forEach(cat => {
          if (cat.category.toLowerCase() === categoryId.toLowerCase()) {
            categoryTools = cat.tools;
          }
        });
        
        if (categoryTools.length === 0) {
          const flat = aiToolsData.flatMap(c => c.tools);
          categoryTools = flat.filter(t => 
            (t.category || "").toLowerCase() === categoryId.toLowerCase() ||
            (t.categories || []).some(c => c.toLowerCase() === categoryId.toLowerCase())
          );
        }
        setTools(categoryTools);
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, [categoryId]);

  const toggleSave = (toolName) => {
    setSaved((prev) => ({
      ...prev,
      [toolName]: !prev[toolName],
    }));
  };

  if (loading) {
    return (
      <div className="category-detail-loading">
        <div className="spinner"></div>
        <p>Loading {categoryId} tools...</p>
      </div>
    );
  }

  return (
    <div className="category-detail-wrapper">
      <div className="category-detail-header">
        <Link to="/" className="back-link"><ArrowLeft size={16} /> Back to Directory</Link>
        <h1>All <span>{categoryId}</span> Tools</h1>
        <p>Discover and compare the best curated {categoryId} solutions available today.</p>
        <span className="category-detail-count">{tools.length} total tools found</span>
      </div>

      {tools.length === 0 ? (
        <div className="empty-results">
          <h2>🔍 No tools found</h2>
          <p>No tools available currently for this category.</p>
        </div>
      ) : (
        <div className="category-detail-grid">
          {tools.map((tool, i) => (
            <div className="tool-card" key={i}>
              <div className="tool-card-rank">#{i + 1}</div>
              <div className="tool-card-top">
                <div className="tool-card-badges">
                  {tool.featured && (
                    <span className="badge badge-featured">
                      <Star size={9} fill="currentColor" /> Featured
                    </span>
                  )}
                  {tool.verified && (
                    <span className="badge badge-verified">
                      <CheckCircle size={9} /> Verified
                    </span>
                  )}
                </div>
                <button
                  className={`tool-bookmark${saved[tool.name] ? " saved" : ""}`}
                  onClick={() => toggleSave(tool.name)}
                >
                  {saved[tool.name] ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                </button>
              </div>

              <div className="tool-card-body">
                <img
                  src={getRealIcon(tool)}
                  alt={tool.name}
                  className="tool-card-icon"
                  onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(tool.name)}&background=1a2b6b&color=fff&size=64`; }}
                />
                <div className="tool-card-info">
                  <div className="tool-card-name-row">
                    <h3 className="tool-card-title">{tool.name}</h3>
                    <span className={`badge-pricing badge-${getRealPricing(tool).toLowerCase().replace(/\s/g, "")}`}>
                      {getRealPricing(tool)}
                    </span>
                  </div>
                  <p className="tool-card-description">{getRealDescription(tool)}</p>
                </div>
              </div>

              <div className="tool-card-tags">
                {(tool.categories || []).slice(0, 3).map((cat, idx) => (
                  <span key={idx} className="tag">{cat}</span>
                ))}
              </div>

              <div className="tool-card-footer">
                <a
                  href={getRealUrl(tool)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tool-card-link"
                >
                  Visit Tool <ExternalLink size={13} />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
